/**
 * DevDock
 *
 * Bottom-docked drawer that surfaces live AI/network telemetry to
 * signed-in admins and DevOps users. Hidden entirely for everyone
 * else — `DebugLogProvider` gates the underlying interceptor too,
 * so there's nothing to render even if a non-admin somehow imported
 * this component directly.
 *
 * Layout
 *   A floating toggle button (bottom-left, desktop only) opens the
 *   drawer; below `lg` the admin header mounts `DevDockLauncher`
 *   instead, so nothing floats over Save buttons and composers on a
 *   phone. The drawer slides up over the bottom ~45% of the viewport, with a header
 *   strip showing the active AI provider (model / URL / masked key),
 *   a toolbar (filter / pause / clear), and a scrollable table of
 *   recent requests. Click any row to expand its request + response
 *   payload.
 *
 * Performance
 *   The dock unmounts when collapsed, so React doesn't re-render the
 *   log table on every captured request. Filter + selected row are
 *   local component state — none of it leaves the component tree.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
    Activity,
    AlertCircle,
    Check,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    ChevronUp,
    Code2,
    Copy,
    Eraser,
    Filter,
    Loader2,
    Pause,
    Play,
    RotateCcw,
    X,
} from 'lucide-react';
import { apiClient } from '@/shared/api';
import { useDebugLog, useDebugLogOptional, type DebugLogEntry } from './debugLogContext';

type EntryFilter = 'all' | 'ai' | 'errors';

interface AiInfo {
    provider: string | null;
    model: string | null;
    server_url: string | null;
    api_key_masked: string;
    is_enabled: boolean;
    request_timeout_seconds: number;
}

// One row from the racing-dispatch CRUD (`/ai-providers`).
// Shape mirrors the AiProvider model's redacted to_dict.
interface ProviderRow {
    id: number;
    name: string;
    kind: string;
    endpoint_url: string | null;
    has_api_key: boolean;
    model: string | null;
    request_timeout_seconds: number;
    is_enabled: boolean;
    priority: number;
}

// Normalized shape rendered by the dock's pager. We collapse the
// legacy single-row config and the racing-dispatch rows into the
// same fields so the pager doesn't need to special-case either.
interface ProviderView {
    // Label shown on the pager — for the legacy row it's "Legacy /
    // single provider"; for racing-dispatch rows it's the user-set name.
    label: string;
    // Kind / provider string (openai, openai_compat, ollama_local, …).
    kind: string;
    model: string;
    serverUrl: string;
    keyDisplay: string;
    is_enabled: boolean;
    request_timeout_seconds: number;
}

const AI_URL_RE = /\/api\/ai\b/;

// Minimum horizontal swipe in pixels to register as a page change.
// Tuned to feel like a deliberate gesture on iOS without conflicting
// with vertical scroll inside the dock's row list.
const SWIPE_THRESHOLD_PX = 40;

function entryMatchesFilter(entry: DebugLogEntry, filter: EntryFilter): boolean {
    if (filter === 'all') return true;
    if (filter === 'ai') return AI_URL_RE.test(entry.url);
    if (filter === 'errors') return entry.status === 'error';
    return true;
}

function formatTimestamp(epochMs: number): string {
    const date = new Date(epochMs);
    const hh = String(date.getHours()).padStart(2, '0');
    const mm = String(date.getMinutes()).padStart(2, '0');
    const ss = String(date.getSeconds()).padStart(2, '0');
    const ms = String(date.getMilliseconds()).padStart(3, '0');
    return `${hh}:${mm}:${ss}.${ms}`;
}

function formatDuration(durationMs: number | null): string {
    if (durationMs == null) return '—';
    if (durationMs < 1000) return `${durationMs}ms`;
    return `${(durationMs / 1000).toFixed(2)}s`;
}

function statusBadgeClasses(entry: DebugLogEntry): string {
    if (entry.status === 'pending') return 'bg-amber-100 text-amber-800';
    if (entry.status === 'error') return 'bg-red-100 text-red-800';
    const code = entry.statusCode ?? 0;
    if (code >= 500) return 'bg-red-100 text-red-800';
    if (code >= 400) return 'bg-orange-100 text-orange-800';
    if (code >= 300) return 'bg-purple-100 text-purple-800';
    return 'bg-emerald-100 text-emerald-800';
}

function statusLabel(entry: DebugLogEntry): string {
    if (entry.status === 'pending') return '…';
    if (entry.statusCode != null) return String(entry.statusCode);
    if (entry.status === 'error') return 'ERR';
    return '—';
}

function copyAsJson(value: unknown): void {
    try {
        const text = JSON.stringify(value, null, 2);
        navigator.clipboard.writeText(text);
    } catch {
        /* clipboard blocked — ignore */
    }
}

function countErrors(entries: DebugLogEntry[]): number {
    return entries.filter((e) => e.status === 'error').length;
}

/**
 * Compact dock launcher for the admin header below `lg`, where the
 * floating button would cover page controls. Renders nothing outside a
 * `DebugLogProvider`, for users without the dev roles, and while the
 * drawer is already open.
 */
export function DevDockLauncher({ className = '' }: { className?: string }) {
    const debugLog = useDebugLogOptional();
    if (!debugLog?.enabled || debugLog.dockOpen) return null;
    const errorCount = countErrors(debugLog.entries);

    return (
        <button
            type="button"
            onClick={() => debugLog.setDockOpen(true)}
            aria-label="Open Developer Dock"
            className={`relative inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-800 text-white hover:bg-slate-700 ${className}`}
        >
            <Code2 size={14} />
            {errorCount > 0 && (
                <span className="absolute -right-1 -top-1 rounded-full bg-red-500 px-1 text-[10px] font-semibold leading-4 text-white">
                    {errorCount}
                </span>
            )}
        </button>
    );
}

export function DevDock() {
    const { entries, paused, enabled, togglePause, clear, dockOpen: open, setDockOpen: setOpen } = useDebugLog();
    const [filter, setFilter] = useState<EntryFilter>('all');
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const [aiInfo, setAiInfo] = useState<AiInfo | null>(null);
    const [providers, setProviders] = useState<ProviderRow[]>([]);
    const [providerIndex, setProviderIndex] = useState(0);
    const swipeStartX = useRef<number | null>(null);

    // Fetch the AI provider strip once whenever the dock opens; we
    // don't poll because the operator can re-open the dock to refresh.
    // We fetch BOTH the legacy single-row config and the racing-
    // dispatch list so the pager can step through every configured
    // backend, not just the legacy one.
    useEffect(() => {
        if (!open || !enabled) return;
        let cancelled = false;
        Promise.allSettled([
            apiClient.get<{ success: boolean; data: AiInfo }>('/debug/ai-info'),
            apiClient.get<{ success: boolean; data: { providers: ProviderRow[] } }>('/ai-providers'),
        ]).then(([infoR, listR]) => {
            if (cancelled) return;
            setAiInfo(infoR.status === 'fulfilled' ? infoR.value.data?.data ?? null : null);
            setProviders(
                listR.status === 'fulfilled'
                    ? (listR.value.data?.data?.providers || [])
                    : []
            );
        });
        return () => {
            cancelled = true;
        };
    }, [open, enabled]);

    // Combined list. Legacy first when it has any config; otherwise
    // start straight with the racing-dispatch rows. The pager goes
    // away entirely when there's only one item to show.
    const providerViews: ProviderView[] = useMemo(() => {
        const out: ProviderView[] = [];
        if (aiInfo && (aiInfo.provider || aiInfo.model || aiInfo.server_url)) {
            out.push({
                label: 'Legacy (single-provider config)',
                kind: aiInfo.provider || '—',
                model: aiInfo.model || '—',
                serverUrl: aiInfo.server_url || '—',
                keyDisplay: aiInfo.api_key_masked || '(none)',
                is_enabled: aiInfo.is_enabled,
                request_timeout_seconds: aiInfo.request_timeout_seconds,
            });
        }
        for (const row of providers) {
            out.push({
                label: row.name,
                kind: row.kind,
                model: row.model || '—',
                serverUrl: row.endpoint_url || '—',
                // The list endpoint deliberately doesn't expose the
                // masked key — only a boolean — so we render that
                // explicitly. (Could surface a masked variant later
                // by extending AiProvider.to_dict.)
                keyDisplay: row.has_api_key ? '••• stored' : '(none)',
                is_enabled: row.is_enabled,
                request_timeout_seconds: row.request_timeout_seconds,
            });
        }
        return out;
    }, [aiInfo, providers]);

    // Clamp the page index when the list shrinks (e.g. an admin
    // deletes a row while the dock is open).
    useEffect(() => {
        if (providerIndex >= providerViews.length && providerViews.length > 0) {
            setProviderIndex(providerViews.length - 1);
        }
    }, [providerIndex, providerViews.length]);

    const currentProvider = providerViews[providerIndex];
    const totalProviders = providerViews.length;
    const showPager = totalProviders > 1;

    const goPrev = () => {
        if (totalProviders === 0) return;
        setProviderIndex((i) => (i - 1 + totalProviders) % totalProviders);
    };
    const goNext = () => {
        if (totalProviders === 0) return;
        setProviderIndex((i) => (i + 1) % totalProviders);
    };

    const onSwipeStart = (e: React.TouchEvent) => {
        swipeStartX.current = e.touches[0]?.clientX ?? null;
    };
    const onSwipeEnd = (e: React.TouchEvent) => {
        if (swipeStartX.current == null) return;
        const dx = (e.changedTouches[0]?.clientX ?? swipeStartX.current) - swipeStartX.current;
        swipeStartX.current = null;
        if (Math.abs(dx) < SWIPE_THRESHOLD_PX) return;
        // Right-swipe → previous (revealing the item behind on the left).
        if (dx > 0) goPrev(); else goNext();
    };

    if (!enabled) return null;

    const visible = entries.filter((e) => entryMatchesFilter(e, filter));
    const errorCount = countErrors(entries);

    const toggleExpanded = (id: string) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    return (
        <>
            {/* Floating launcher, desktop only. Position-fixed so it
                rides above any page content; sits opposite the chat
                widget so they don't overlap. Below `lg` the admin
                header's DevDockLauncher takes over, so the button never
                covers a Save bar or a composer on a phone. */}
            {!open && (
                <button
                    onClick={() => setOpen(true)}
                    aria-label="Open Developer Dock"
                    className="fixed bottom-5 left-5 z-40 hidden items-center gap-1.5 rounded-full bg-slate-800 px-3 py-2 text-xs font-medium text-white shadow-lg hover:bg-slate-700 lg:inline-flex"
                >
                    <Code2 size={14} />
                    Dev
                    {errorCount > 0 && (
                        <span className="ml-0.5 rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                            {errorCount}
                        </span>
                    )}
                </button>
            )}

            {open && (
                <div
                    className="fixed inset-x-0 bottom-0 z-40 flex h-[45vh] flex-col border-t-4 border-slate-800 bg-white shadow-2xl"
                    role="region"
                    aria-label="Developer Dock"
                >
                    {/* AI config strip — paged across every configured
                        provider. Swipe left/right on touch, or use the
                        chevron buttons. Hidden chrome when there's only
                        one item to look at. */}
                    <div
                        className="flex flex-wrap items-center gap-x-4 gap-y-1 bg-slate-800 px-4 py-2 text-xs text-slate-200"
                        onTouchStart={onSwipeStart}
                        onTouchEnd={onSwipeEnd}
                    >
                        <div className="flex items-center gap-2 font-semibold text-white">
                            <Code2 size={14} />
                            Developer Dock
                        </div>
                        {showPager && (
                            <div className="flex items-center gap-1 rounded-md bg-slate-700/60 px-1.5 py-0.5">
                                <button
                                    type="button"
                                    onClick={goPrev}
                                    aria-label="Previous provider"
                                    className="rounded p-0.5 text-slate-200 hover:bg-slate-600 hover:text-white"
                                >
                                    <ChevronLeft size={14} />
                                </button>
                                <span className="font-mono text-[11px] text-slate-100">
                                    {providerIndex + 1} / {totalProviders}
                                </span>
                                <button
                                    type="button"
                                    onClick={goNext}
                                    aria-label="Next provider"
                                    className="rounded p-0.5 text-slate-200 hover:bg-slate-600 hover:text-white"
                                >
                                    <ChevronRight size={14} />
                                </button>
                            </div>
                        )}
                        {currentProvider ? (
                            <>
                                {showPager && (
                                    <span className="font-semibold text-white">
                                        {currentProvider.label}
                                    </span>
                                )}
                                <span>
                                    Provider:{' '}
                                    <span className="font-mono text-amber-200">{currentProvider.kind}</span>
                                </span>
                                <span>
                                    Model:{' '}
                                    <span className="font-mono text-amber-200">{currentProvider.model}</span>
                                </span>
                                <span>
                                    URL:{' '}
                                    <span className="font-mono text-amber-200 break-all">
                                        {currentProvider.serverUrl}
                                    </span>
                                </span>
                                <span>
                                    Key:{' '}
                                    <span className="font-mono text-amber-200">
                                        {currentProvider.keyDisplay}
                                    </span>
                                </span>
                                <span>
                                    Timeout:{' '}
                                    <span className="font-mono text-amber-200">
                                        {currentProvider.request_timeout_seconds}s
                                    </span>
                                </span>
                                <span>
                                    Status:{' '}
                                    <span
                                        className={`font-mono ${currentProvider.is_enabled ? 'text-emerald-300' : 'text-red-300'}`}
                                    >
                                        {currentProvider.is_enabled ? 'enabled' : 'disabled'}
                                    </span>
                                </span>
                            </>
                        ) : (
                            <span className="text-slate-400">Loading AI config…</span>
                        )}
                        <div className="ml-auto flex items-center gap-1">
                            <button
                                onClick={() => setOpen(false)}
                                aria-label="Close Developer Dock"
                                className="rounded-md p-1 text-slate-300 hover:bg-slate-700 hover:text-white"
                            >
                                <X size={16} />
                            </button>
                        </div>
                    </div>

                    {/* Toolbar */}
                    <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-4 py-2 text-xs">
                        <div className="inline-flex items-center gap-1 text-gray-500">
                            <Filter size={12} />
                            <span>Filter:</span>
                        </div>
                        {(['all', 'ai', 'errors'] as EntryFilter[]).map((key) => (
                            <button
                                key={key}
                                onClick={() => setFilter(key)}
                                className={`rounded-md px-2 py-1 text-[11px] font-medium ${
                                    filter === key
                                        ? 'bg-slate-800 text-white'
                                        : 'bg-white text-gray-600 hover:bg-gray-100'
                                }`}
                            >
                                {key === 'all' && `All (${entries.length})`}
                                {key === 'ai' && `AI (${entries.filter((e) => AI_URL_RE.test(e.url)).length})`}
                                {key === 'errors' && `Errors (${errorCount})`}
                            </button>
                        ))}

                        <div className="ml-auto flex items-center gap-1">
                            {/* Network probe — fires GET /api/debug/network-probe;
                                the response lands in the log table just like any
                                other call, with DNS / TCP / TLS / HTTP phase
                                timings in the response body for the operator to
                                expand. Skips its own state because the
                                interceptor already captures everything. */}
                            <button
                                onClick={() => {
                                    apiClient.get('/debug/network-probe').catch(() => {
                                        /* logged via interceptor; nothing to do here */
                                    });
                                }}
                                className="inline-flex items-center gap-1 rounded-md bg-white px-2 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-100"
                                title="Probe DNS / TCP / TLS / HTTP latency to the AI server"
                            >
                                <Activity size={12} />
                                Probe AI server
                            </button>
                            {/* Reset chat — clears the visitor's
                                client_id from localStorage so the next
                                page load starts a brand-new
                                AiChatSession from the intake step. The
                                old session row stays in the DB
                                (harmless; orphaned). Saves the operator
                                from juggling incognito windows when
                                testing the intake flow. */}
                            <button
                                onClick={() => {
                                    if (!confirm(
                                        'Reset the chat? You\'ll lose your local Vero session and the intake will restart on the next page load.',
                                    )) return;
                                    try {
                                        localStorage.removeItem('aiChat.clientId.v1');
                                    } catch {
                                        /* private mode — nothing to clear */
                                    }
                                    window.location.reload();
                                }}
                                className="inline-flex items-center gap-1 rounded-md bg-white px-2 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-100"
                                title="Clear the Vero client_id from localStorage and reload"
                            >
                                <RotateCcw size={12} />
                                Reset chat
                            </button>
                            <button
                                onClick={togglePause}
                                className="inline-flex items-center gap-1 rounded-md bg-white px-2 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-100"
                            >
                                {paused ? <Play size={12} /> : <Pause size={12} />}
                                {paused ? 'Resume' : 'Pause'}
                            </button>
                            <button
                                onClick={clear}
                                className="inline-flex items-center gap-1 rounded-md bg-white px-2 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-100"
                            >
                                <Eraser size={12} />
                                Clear
                            </button>
                        </div>
                    </div>

                    {/* Log table */}
                    <div className="flex-1 overflow-y-auto">
                        {visible.length === 0 ? (
                            <div className="flex h-full items-center justify-center text-xs text-gray-400">
                                {paused
                                    ? 'Capture is paused.'
                                    : 'No requests captured yet — interact with the site to fill this in.'}
                            </div>
                        ) : (
                            <table className="w-full text-left text-xs">
                                <thead className="sticky top-0 bg-white text-[11px] uppercase tracking-wide text-gray-500">
                                    <tr>
                                        <th className="w-28 px-3 py-1.5 font-medium">Time</th>
                                        <th className="w-16 px-2 py-1.5 font-medium">Method</th>
                                        <th className="px-2 py-1.5 font-medium">URL</th>
                                        <th className="w-20 px-2 py-1.5 font-medium">Status</th>
                                        <th className="w-20 px-2 py-1.5 font-medium">Duration</th>
                                        <th className="w-6 px-2 py-1.5" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {visible.map((entry) => (
                                        <DevDockRow
                                            key={entry.id}
                                            entry={entry}
                                            expanded={expanded.has(entry.id)}
                                            onToggle={() => toggleExpanded(entry.id)}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}

interface DevDockRowProps {
    entry: DebugLogEntry;
    expanded: boolean;
    onToggle: () => void;
}

function DevDockRow({ entry, expanded, onToggle }: DevDockRowProps) {
    return (
        <>
            <tr
                onClick={onToggle}
                className={`cursor-pointer border-b border-gray-100 hover:bg-gray-50 ${expanded ? 'bg-gray-50' : ''}`}
            >
                <td className="px-3 py-1.5 font-mono text-[11px] text-gray-500">
                    {formatTimestamp(entry.timestamp)}
                </td>
                <td className="px-2 py-1.5 font-mono text-[11px] font-semibold text-gray-700">
                    {entry.method}
                </td>
                <td className="px-2 py-1.5 truncate font-mono text-[11px] text-gray-800">
                    {entry.url}
                </td>
                <td className="px-2 py-1.5">
                    <span
                        className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold ${statusBadgeClasses(entry)}`}
                    >
                        {entry.status === 'pending' && <Loader2 size={10} className="animate-spin" />}
                        {entry.status === 'ok' && <Check size={10} />}
                        {entry.status === 'error' && <AlertCircle size={10} />}
                        {statusLabel(entry)}
                    </span>
                </td>
                <td className="px-2 py-1.5 font-mono text-[11px] text-gray-500">
                    {formatDuration(entry.durationMs)}
                </td>
                <td className="px-2 py-1.5 text-gray-400">
                    {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                </td>
            </tr>
            {expanded && (
                <tr className="border-b border-gray-100 bg-slate-900 text-slate-100">
                    <td colSpan={6} className="px-3 py-3">
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                            <DevDockPayloadBlock label="Request headers" payload={entry.requestHeaders} />
                            <DevDockPayloadBlock label="Request body" payload={entry.requestBody} />
                            <DevDockPayloadBlock label="Response body" payload={entry.responseBody} />
                            {entry.errorMessage && (
                                <div>
                                    <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-red-300">
                                        Error
                                    </div>
                                    <pre className="overflow-x-auto rounded bg-red-900/40 p-2 text-[11px] text-red-100">
                                        {entry.errorMessage}
                                    </pre>
                                </div>
                            )}
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
}

interface DevDockPayloadBlockProps {
    label: string;
    payload: unknown;
}

function DevDockPayloadBlock({ label, payload }: DevDockPayloadBlockProps) {
    if (payload == null) return null;
    const json = (() => {
        try {
            return JSON.stringify(payload, null, 2);
        } catch {
            return String(payload);
        }
    })();
    return (
        <div className="min-w-0">
            <div className="mb-1 flex items-center justify-between gap-2">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                    {label}
                </span>
                <button
                    onClick={() => copyAsJson(payload)}
                    className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] text-slate-300 hover:bg-slate-700 hover:text-white"
                >
                    <Copy size={10} />
                    Copy
                </button>
            </div>
            <pre className="max-h-48 overflow-auto rounded bg-slate-800/80 p-2 text-[11px] leading-snug text-slate-100">
                {json}
            </pre>
        </div>
    );
}

export default DevDock;
